import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { AdminLayout } from "./AdminDashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Ticket, Search, Download, CheckCircle, Gift, Plus, Trash2, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AdminTickets() {
  const [tickets, setTickets] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEvent, setSelectedEvent] = useState("all");
  const [compOpen, setCompOpen] = useState(false);
  const [compForm, setCompForm] = useState({ event_id: "", ticket_type_name: "Complimentary", attendee_name: "", attendee_email: "", quantity: 1, notes: "" });
  const [compCreating, setCompCreating] = useState(false);
  const [deletingTicketId, setDeletingTicketId] = useState("");

  const handleDeleteTicket = async (ticket) => {
    const isStripe = ticket.payment_method === "stripe" && ticket.payment_status === "paid";
    if (isStripe) {
      toast.error("Cannot delete a paid Stripe ticket. Refund through Stripe first.");
      return;
    }
    const label = `${ticket.attendee_name || "Guest"} — ${ticket.event_title || "Event"} (${ticket.ticket_type_name || "General"})`;
    if (!window.confirm(`Delete this ticket?\n\n${label}\n\nThis removes it from the attendee's wallet and decrements the event's sold count. Cannot be undone.`)) return;
    setDeletingTicketId(ticket.ticket_id);
    try {
      const res = await axios.delete(`${API}/admin/tickets/${ticket.ticket_id}`, { withCredentials: true });
      toast.success(res.data?.message || "Ticket deleted");
      await fetchTickets();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete ticket");
    } finally { setDeletingTicketId(""); }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [eventsRes] = await Promise.all([
        axios.get(`${API}/events?upcoming=false`, { withCredentials: true })
      ]);
      setEvents(eventsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
  }, [selectedEvent]);

  const fetchTickets = async () => {
    try {
      if (selectedEvent && selectedEvent !== "all") {
        const response = await axios.get(`${API}/tickets/event/${selectedEvent}`, { withCredentials: true });
        setTickets(response.data);
      } else {
        // Fetch ALL tickets across all events
        const response = await axios.get(`${API}/admin/all-tickets`, { withCredentials: true });
        setTickets(response.data);
      }
    } catch (error) {
      console.error("Error fetching tickets:", error);
    }
  };

  const filteredTickets = tickets.filter(t => 
    t.attendee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.attendee_email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.ticket_id?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const checkedInCount = tickets.filter(t => t.status === "used").length;

  const exportCSV = () => {
    const headers = ["Ticket ID", "Attendee", "Email", "Status", "Checked In At"];
    const rows = tickets.map(t => [
      t.ticket_id,
      t.attendee_name,
      t.attendee_email,
      t.status,
      t.checked_in_at ? format(new Date(t.checked_in_at), "yyyy-MM-dd HH:mm") : ""
    ]);
    
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `tickets-${selectedEvent}-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
  };

  const handleManualCheckIn = async (ticketId) => {
    try {
      await axios.post(`${API}/tickets/checkin`, { ticket_id: ticketId }, { withCredentials: true });
      toast.success("Ticket checked in");
      fetchTickets();
    } catch (error) {
      toast.error("Check-in failed");
    }
  };

  return (
    <AdminLayout currentPage="Tickets">
      <div className="p-8" data-testid="admin-tickets">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-heading-2 text-deep-blue">Tickets</h1>
            <p className="text-slate-600">Manage event tickets and check-ins</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setCompOpen(true)} className="bg-deep-blue text-white" data-testid="comp-ticket-btn">
              <Gift className="w-4 h-4 mr-2" /> Comp Tickets
            </Button>
            {selectedEvent && selectedEvent !== "all" && (
              <Button variant="outline" onClick={exportCSV} data-testid="export-tickets-btn">
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
            )}
          </div>
        </div>

        {/* Comp Ticket Dialog */}
        <Dialog open={compOpen} onOpenChange={setCompOpen}>
          <DialogContent className="sm:max-w-md" data-testid="comp-ticket-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2"><Gift className="w-5 h-5" /> Create Comp Tickets</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Event *</Label>
                <Select value={compForm.event_id} onValueChange={(v) => setCompForm(p => ({...p, event_id: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select event" /></SelectTrigger>
                  <SelectContent>{events.map(e => <SelectItem key={e.event_id} value={e.event_id}>{e.title}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Name *</Label><Input value={compForm.attendee_name} onChange={(e) => setCompForm(p => ({...p, attendee_name: e.target.value}))} placeholder="VIP Name" data-testid="comp-name" /></div>
                <div><Label>Email</Label><Input value={compForm.attendee_email} onChange={(e) => setCompForm(p => ({...p, attendee_email: e.target.value}))} placeholder="email@..." data-testid="comp-email" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label>Ticket Type</Label><Input value={compForm.ticket_type_name} onChange={(e) => setCompForm(p => ({...p, ticket_type_name: e.target.value}))} placeholder="VIP / Complimentary" /></div>
                <div><Label>Quantity</Label><Input type="number" min={1} max={20} value={compForm.quantity} onChange={(e) => setCompForm(p => ({...p, quantity: parseInt(e.target.value) || 1}))} /></div>
              </div>
              <div><Label>Notes</Label><Input value={compForm.notes} onChange={(e) => setCompForm(p => ({...p, notes: e.target.value}))} placeholder="e.g., Board member, Speaker" /></div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setCompOpen(false)}>Cancel</Button>
                <Button disabled={compCreating || !compForm.event_id || !compForm.attendee_name} className="bg-deep-blue text-white" data-testid="comp-create-btn"
                  onClick={async () => {
                    setCompCreating(true);
                    try {
                      const res = await axios.post(`${API}/admin/comp-tickets`, compForm, { withCredentials: true });
                      toast.success(`${res.data.count} comp ticket(s) created!`);
                      setCompOpen(false);
                      setCompForm({ event_id: "", ticket_type_name: "Complimentary", attendee_name: "", attendee_email: "", quantity: 1, notes: "" });
                      fetchTickets();
                    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
                    finally { setCompCreating(false); }
                  }}>
                  {compCreating ? "Creating..." : "Create Comp Tickets"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Event Selection */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-slate-700 mb-2">Select Event</label>
          <Select value={selectedEvent} onValueChange={setSelectedEvent}>
            <SelectTrigger className="w-80" data-testid="event-select">
              <SelectValue placeholder="Select an event" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Select an event...</SelectItem>
              {events.map(event => (
                <SelectItem key={event.event_id} value={event.event_id}>
                  {event.title} - {format(new Date(event.date), "MMM d, yyyy")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <>
          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-sm text-slate-500">Total Tickets</p>
              <p className="text-3xl font-bold text-deep-blue">{tickets.length}</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-sm text-slate-500">Valid</p>
              <p className="text-3xl font-bold text-green-600">{tickets.filter(t => t.status === "valid").length}</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-sm text-slate-500">Used / Checked In</p>
              <p className="text-3xl font-bold text-awareness-blue">{checkedInCount}</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <p className="text-sm text-slate-500">Comp Tickets</p>
              <p className="text-3xl font-bold text-amber-600">{tickets.filter(t => t.is_comp).length}</p>
            </div>
          </div>

            {/* Search */}
            <div className="mb-6">
              <div className="relative w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input 
                  placeholder="Search tickets..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-tickets"
                />
              </div>
            </div>

            {/* Tickets Table */}
            {filteredTickets.length > 0 ? (
              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="text-left p-4 font-semibold text-slate-700 text-sm">Attendee</th>
                      <th className="text-left p-4 font-semibold text-slate-700 text-sm">Event / Type</th>
                      <th className="text-left p-4 font-semibold text-slate-700 text-sm">Status</th>
                      <th className="text-left p-4 font-semibold text-slate-700 text-sm">Payment</th>
                      <th className="text-left p-4 font-semibold text-slate-700 text-sm">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredTickets.map(ticket => (
                      <tr key={ticket.ticket_id} data-testid={`ticket-row-${ticket.ticket_id}`}>
                        <td className="p-4">
                          <p className="font-medium text-slate-800">{ticket.attendee_name}</p>
                          <p className="text-sm text-slate-500">{ticket.attendee_email}</p>
                          <p className="text-xs text-slate-400 font-mono mt-0.5">{ticket.ticket_id}</p>
                        </td>
                        <td className="p-4">
                          <p className="text-sm font-medium text-deep-blue">{ticket.event_title || "—"}</p>
                          <p className="text-xs text-slate-500">{ticket.ticket_type_name || "General"}</p>
                        </td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs font-bold ${
                            ticket.status === 'valid' ? 'bg-green-100 text-green-700' :
                            ticket.status === 'used' ? 'bg-blue-100 text-blue-700' :
                            ticket.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                            'bg-slate-100 text-slate-600'
                          }`}>
                            {ticket.status === 'valid' ? 'VALID' : ticket.status === 'used' ? 'USED' : ticket.status?.toUpperCase()}
                          </span>
                          {ticket.is_comp && <span className="ml-1 px-2 py-1 rounded text-xs font-bold bg-amber-100 text-amber-700">COMP</span>}
                        </td>
                        <td className="p-4">
                          <span className={`text-xs font-semibold ${ticket.payment_status === 'paid' ? 'text-green-600' : ticket.payment_status === 'comp' ? 'text-amber-600' : 'text-slate-500'}`}>
                            {ticket.payment_method === 'comp' ? 'FREE' : ticket.payment_status === 'paid' ? `$${ticket.price_paid?.toFixed(2) || '0.00'}` : 'PENDING'}
                          </span>
                          <p className="text-[10px] text-slate-400">{ticket.payment_method || "—"}</p>
                        </td>
                        <td className="p-4">
                          <div className="flex flex-wrap gap-1.5">
                            {ticket.status === "valid" && (
                              <Button size="sm" variant="outline" onClick={() => handleManualCheckIn(ticket.ticket_id)} data-testid={`checkin-${ticket.ticket_id}`}>
                                <CheckCircle className="w-3 h-3 mr-1" /> Check In
                              </Button>
                            )}
                            {ticket.status === "used" && <span className="text-xs text-blue-600">{ticket.checked_in_at ? format(new Date(ticket.checked_in_at), "MMM d, h:mm a") : "Checked in"}</span>}
                            {!(ticket.payment_method === "stripe" && ticket.payment_status === "paid") && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                onClick={() => handleDeleteTicket(ticket)}
                                disabled={deletingTicketId === ticket.ticket_id}
                                data-testid={`delete-ticket-${ticket.ticket_id}`}
                                title="Delete this ticket (only available for non-Stripe tickets)"
                              >
                                {deletingTicketId === ticket.ticket_id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Trash2 className="w-3 h-3" />}
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bg-white rounded-lg p-12 text-center">
                <Ticket className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-700 mb-2">No tickets found</h3>
                <p className="text-slate-500">No tickets match your search</p>
              </div>
            )}
          </>
      </div>
    </AdminLayout>
  );
}
